/**
 * "Upcoming" on a set that has not come out yet.
 *
 * The catalogue now carries sets before their release, from presale vendor data
 * (see supabase/functions/catalogue-presale). Those look exactly like any other
 * set - real cards, real numbers, real artwork, real prices - and without a mark
 * the only honest reading of a set showing 0 of 26 owned is that you are missing
 * all of it, when in fact nobody on earth owns one yet.
 *
 * The rule is the release date, not where the row came from. A set is upcoming
 * when it has not been released, whichever source told us about it, and it stops
 * being upcoming on the day it lands with no further work.
 */
import { useMemo } from 'react';

/** Today at midnight, so a set released today is out rather than upcoming. */
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Whether a set is still to come.
 *
 * @param {{releaseDate?: string, release_date?: string}} set
 * @param {Date} [now] injectable for tests
 */
export function isUpcomingSet(set, now = startOfToday()) {
  const raw = set?.releaseDate ?? set?.release_date ?? null;
  if (!raw) return false;
  const when = new Date(`${String(raw).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(when.getTime())) return false;
  return when.getTime() > now.getTime();
}

/**
 * How far off it is, in the words a person would use.
 *
 * Deliberately vague past a fortnight. A precise "in 54 days" invites someone to
 * check back daily; "November" is what they actually need to know.
 */
export function releaseLine(set, now = startOfToday()) {
  const raw = set?.releaseDate ?? set?.release_date ?? null;
  if (!raw) return null;
  const when = new Date(`${String(raw).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(when.getTime())) return null;
  const days = Math.round((when.getTime() - now.getTime()) / 86400000);
  if (days <= 0) return null;
  if (days === 1) return 'Out tomorrow';
  if (days <= 14) return `Out in ${days} days`;
  return `Out ${when.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
}

/**
 * The badge itself.
 *
 * @param {object} props
 * @param {object} props.set        the set row, for its release date
 * @param {object} props.theme      the app theme, for the surface it sits on
 * @param {boolean} props.withDate  also say when, where there is room for it
 * @param {object} props.style      placement; the caller owns where it sits
 */
export default function SetUpcomingBadge({ set, theme, withDate = false, style = null }) {
  const line = useMemo(() => releaseLine(set), [set]);
  if (!isUpcomingSet(set)) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '3px 8px',
        borderRadius: '999px',
        backgroundColor: 'rgba(245, 158, 11, 0.16)',
        color: '#f59e0b',
        border: '1px solid rgba(245, 158, 11, 0.38)',
        fontSize: '9px',
        fontWeight: '800',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        ...(style || {}),
      }}
    >
      {/* A drawn dot rather than a character, so it cannot be an emoji and
          cannot pick up a font that has no glyph for it. */}
      <span
        style={{
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          backgroundColor: '#f59e0b',
          flexShrink: 0,
        }}
      />
      {withDate && line ? line : 'Upcoming'}
    </span>
  );
}
